<div class="privacy-container">

  <div class="privacy-content">
    <h1 class="privacy-title">Privacy Policy</h1>

    <section class="privacy-section">
      <h2 class="section-title">1. Introduction</h2>
      <p>
        Stitch provides the https://stitch.agi.supply website (the "Website"). Stitch is committed to respecting your privacy and data protection laws and regulations. This Privacy Policy (the "Policy") intends to inform you about the way we use your data (i.e., any data related to an identifiable natural person, either directly or indirectly), whether we collect them when you visit our Website.
      </p>
      <p>
        By accessing and using this Website, you confirm that you have read and fully understood this Policy, that you agree to the collection and the usage of your own and others' data following the Policy, and that you have the authority to provide Stitch with all information submitted by you via the Website, including but not limited to the personal data of third parties.
      </p>
    </section>

    <section class="privacy-section">
      <h2 class="section-title">2. General Principles of the Processing</h2>
      <p>
        We collect and process personal data transparently, to the extent necessary for specified, explicit, and legitimate purposes, and do not process it further in a manner incompatible with those purposes. We take care that the data we collect are accurate and, when necessary, updated. We take all reasonable steps to immediately delete or rectify personal data, if inaccurate. We process data in a way that guarantees their security, including their protection against unauthorized or unlawful processing and accidental loss, destruction, or degradation, using appropriate technical or organizational measures. We are ready to prove at any moment how we adhere to the above principles. We take the appropriate technical and organizational measures for the security, confidentiality, integrity, and availability of the data. These measures ensure that, by definition, personal data are not made accessible without the intervention of the natural person to an indeterminate number of natural persons. We respect your rights under the General Data Protection Regulation (GDPR) and applicable data protection laws and regulations.
      </p>
    </section>

    <section class="privacy-section">
      <h2 class="section-title">3. Usage of data we collect about you</h2>
      <h3 class="subsection-title">3.1 When visiting our Website</h3>
      <p>
        Stitch is the "Data Controller" of all personal data collected through the Website. This means that Stitch determines the means and the purposes of the processing and is responsible for replying to your requests about using your data.
      </p>

      <h4 class="subsection-title">3.1.1 COOKIES</h4>
      <p>
        A cookie is a small file your browser stores on your device's hard disk for record-keeping purposes. Namely, it records information about the use and activity of the Website. This information may include, but is not limited to, your Internet Protocol address, browser type, and the user's web browsing history before visiting the Website, our Website's search history. Some cookies are "first-party cookies," meaning we set them. Cookies set by parties other than us are called "third-party cookies."
      </p>
      <p>
        Cookies are used for different reasons.
      </p>
      <p>
        There are the necessary cookies, which are required for technical reasons for a website to operate. Some cookies are used to enhance the performance and functionality of a website but are non-essential to their use. However, certain functionality may become unavailable if you decide not to accept such cookies. Such cookies are called preferences cookies. Some cookies collect information that is used in aggregate form to help a website owner understand how a website is being used. Such cookies are called statistic cookies, either first-party or third-party cookies. A third party, for example, Google, stores a Google Analytics cookie to be able to differentiate between users and be able to show a website owner how many times people visit a website on average (not individually) and information on what pages they've seen, how long the duration was, and so on. Some cookies are used for marketing purposes. These are the "marketing cookies" and are third-party cookies. Third-party cookies are placed by providers (e.g., Google, Facebook) whom a website owner may have engaged to provide advertising services on their behalf. If visitors of a webpage are interested in one of the services from the analysis of information collected by third-party cookies, then advertising material would be projected on third-party websites. You can visit third parties' respective websites to see how data is collected and analyzed by third-party cookies.
      </p>
      <p>
        When you visit our Website, you are asked to consent to the use of cookies, and our Cookies Notice informs you about the type of Cookies we use, their provider, the kind of data they collect, their purpose (e.g., session, personalization, marketing, etc.) and their expiry. You may consent to none, one or more of the above cookies. You may withdraw your consent to using cookies at any time during your visit to our Website freely and easily by clicking on the "Manage Cookies Settings" button next to our "Privacy Policy" button.
      </p>
      <p>
        Additionally, by changing its options, you can instruct your browser to stop accepting cookies or prompt you before accepting a cookie from the websites you visit.
      </p>

      <h4 class="subsection-title">3.1.2 CONTACT FORM</h4>
      <p>
        If you wish to communicate with us using the contact form, please enter your name, e-mail address, telephone number, and the matter you want to discuss with us and write your message in the dedicated space. Such personal data is used solely to respond to you, and we keep your data only as long as it is necessary to respond to your request.
      </p>

      <h3 class="subsection-title">3.2 Google User Data Access (Google Ads API &amp; OAuth)</h3>
      <p>
        Our application allows users to connect their Google account via Google OAuth 2.0 in order to access Google Ads data.
      </p>
      <p>
        We request the following scope:
      </p>
      <p>
        <code>https://www.googleapis.com/auth/adwords</code>
      </p>
      <p>
        This permission allows our application to:
      </p>
      <ul class="privacy-list">
        <li>Retrieve Google Ads account structure (including manager and sub-accounts)</li>
        <li>Access campaign, ad group, and keyword data</li>
        <li>Retrieve performance metrics and reports</li>
      </ul>
      <p>
        The level of access strictly depends on the permissions granted to the user's Google account. If the user has limited or read-only access, the application will only access data accordingly.
      </p>
      <p>
        We do not access any Google user data beyond what is necessary to provide the core functionality of the service.
      </p>

      <h3 class="subsection-title">3.3 Use of Google User Data</h3>
      <p>
        Google user data is used exclusively for:
      </p>
      <ul class="privacy-list">
        <li>Displaying Google Ads data within the application</li>
        <li>Providing analytics and insights</li>
        <li>Generating reports and keyword suggestions</li>
      </ul>
      <p>
        We do not use Google user data for advertising, resale, or profiling outside the functionality of the application.
      </p>
    </section>

    <section class="privacy-section">
      <h2 class="section-title">4. Security</h2>
      <p>
        When we collect personal data as Data Processors, we take appropriate technical and organizational security measures to protect the integrity, accessibility, and confidentiality of personal data. These measures are physical and environmental security measures and IT security measures, including but not limited to the use of updated anti-virus and firewalls, safe protocols, user authentication processes, encryption, data separation, vulnerability and penetration tests, etc.
      </p>

      <h3 class="subsection-title">Google OAuth Data Security</h3>
      <p>
        We securely store limited Google OAuth credentials on our backend infrastructure.
      </p>
      <p>
        The following sensitive data is stored:
      </p>
      <ul class="privacy-list">
        <li>OAuth access tokens</li>
        <li>OAuth refresh tokens</li>
        <li>Client credentials</li>
      </ul>
      <p>
        All sensitive data is encrypted before storage using:
      </p>
      <ul class="privacy-list">
        <li>Per-user unique salt values</li>
        <li>A service-level encryption secret</li>
      </ul>
      <p>
        This ensures that sensitive data is never stored in plain text.
      </p>
      <p>
        Access to this data is strictly limited and used only for authorized communication with Google APIs.
      </p>
    </section>

    <section class="privacy-section">
      <h2 class="section-title">5. Sharing your Data – International Data Transfer</h2>
      <h3 class="subsection-title">5.1 Data Sharing</h3>
      <p>
        We do not sell or rent Google user data to third parties.
      </p>
      <p>
        Google user data is used strictly within our system to provide application functionality.
      </p>
      <p>
        Our system includes an internal service that interacts with the Google Ads API using authorized credentials. This service:
      </p>
      <ul class="privacy-list">
        <li>does not store user data</li>
        <li>only processes requests on behalf of the user</li>
      </ul>
      <p>
        We do not transfer Google user data to external third-party services.
      </p>

      <h3 class="subsection-title">5.2 Social media</h3>
      <p>
        Using social media plugins, you can access third-party social media services through our Website. In that case, personal data may be transferred to this third party based on this party's Privacy Policy. Make sure you read the Privacy Policy of this third party before you proceed to the plugin. Our Website reminds you of that before it activates the uses of the relevant social media plugins.
      </p>

      <h3 class="subsection-title">5.3 Data Retention</h3>
      <p>
        We retain Google user data only for as long as necessary to provide the service.
      </p>
      <p>
        OAuth credentials and related data are stored until:
      </p>
      <ul class="privacy-list">
        <li>the user disconnects their Google account</li>
        <li>the user deletes their account</li>
      </ul>
      <p>
        Expired or inactive data may be periodically deleted.
      </p>
    </section>

    <section class="privacy-section">
      <h2 class="section-title">6. Your Rights</h2>
      <p>
        We respect your rights as a data subject under the GDPR and other applicable data protection laws and regulations. When acting as a Data Processors, the Data Controller addresses your requests. However, we shall provide reasonable assistance to the Data Controller to satisfy your rights.
      </p>

      <h3 class="subsection-title">6.1 Information and Transparency</h3>
      <p>
        You have the right to be informed about any processing of your data (the purpose, scope, duration, and means, as well as data sharing). We adhere to the principle of transparency in processing. For any questions regarding this Policy, please contact us at: <a href="mailto:hi@brnz.ai">hi&#64;brnz.ai</a>. We will respond without delay and, in any case, within one month upon receipt of the request.
      </p>

      <h3 class="subsection-title">6.2 Erasure</h3>
      <p>
        You have the right to ask for the erasure of personal data concerning you without undue delay.
      </p>
      <p>
        We shall erase the data where one of the following grounds applies:
      </p>
      <ul class="privacy-list">
        <li>personal data is no longer necessary for processing;</li>
        <li>the person requesting the erasure withdraws consent on which the processing is based (for example, in the case of our newsletter), and there is no other legal ground for the processing;</li>
        <li>the data subject objects to the processing, and there are no overriding legitimate grounds for the processing or the data subject objects to processing for direct marketing;</li>
        <li>the personal data must be erased in compliance with a legal obligation.</li>
      </ul>

      <h4 class="subsection-title">Google User Data Deletion</h4>
      <p>
        Users may request deletion of their Google user data by:
      </p>
      <ul class="privacy-list">
        <li>disconnecting their Google account within the application, or</li>
        <li>contacting us at: <a href="mailto:hi@brnz.ai">hi&#64;brnz.ai</a></li>
      </ul>
      <p>
        Upon request, we will delete all associated Google OAuth data, including stored credentials, within a reasonable timeframe.
      </p>

      <p>
        We shall not proceed to erasure the personal data if the data must be maintained for compliance with a legal obligation or in cases where the processing is required for the establishment, exercise, or defense of legal claims. You can also delete data stored in your Account or your Account, as described earlier in this Policy.
      </p>

      <h3 class="subsection-title">6.3 Restriction of processing</h3>
      <p>
        You have the right to request the restriction of processing if the accuracy of personal data is disputed for that period that allows Stitch to verify the accuracy of personal data or based on any other legitimate reason specified in applicable data protection laws. For example, ask us to suspend the processing of your data if you want us to establish its accuracy or the reason for processing it.
      </p>

      <h3 class="subsection-title">6.4 Data Portability</h3>
      <p>
        You have the right to receive your Data in a structured, commonly used, and machine-readable format and request the direct transmission of personal data by another to another (controller or processor) if this is feasible.
      </p>

      <h3 class="subsection-title">6.5 Right to Object</h3>
      <p>
        You may oppose processing personal data, which takes place based on an overriding legitimate interest of a Data Controller without your consent. In this case, we no longer process your data unless it demonstrates imperative and legitimate reasons for the processing that outweigh the interests, rights, and freedoms of you as a data subject or for the establishment, exercise, or defense of legal claims.
      </p>
      <p>
        If you have ordered and received our Newsletter, you can, easily and without cost, remove yourself from the recipients' list by selecting the "unsubscribe" link within the email content. You may also oppose processing at any time by contacting us at the email provided at the end of this Policy.
      </p>

      <h3 class="subsection-title">6.6 No automated individual decision-making</h3>
      <p>
        We fully respect your right not to be subject to a decision based solely on automated processing, including profiling, which produces legal effects concerning you or similarly significantly affects you. You have the right to object to such automated individual decision-making.
      </p>

      <h3 class="subsection-title">6.7 Consent Withdrawal</h3>
      <p>
        Suppose you have consented to collecting, processing, and transferring your data for a specific purpose. In that case, you can withdraw your consent for that particular processing time. Once we have received notification that you have withdrawn your consent, we will only process your information for the purpose or purposes you originally agreed to if we have another legitimate basis for doing so in law. In this case, we will notify you. For example, you may withdraw your consent to cookies by clicking "Manage Cookies Settings" or choose not to accept cookies by your browser settings.
      </p>

      <h3 class="subsection-title">6.8 Children</h3>
      <p>
        Our services are not directed to children. We do not knowingly collect personal data from children. We will immediately delete such information if we become aware that a child has provided us with personal data. If you know a child has provided us with their data, please get in touch with us at the contact information below.
      </p>
    </section>

    <section class="privacy-section">
      <h2 class="section-title">7. Compliance with Google API Services User Data Policy</h2>
      <p>
          Our use of Google user data complies with the <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer">Google API Services User Data Policy</a>, including the Limited Use requirements.
      </p>
      <p>
          We only access, use, and store Google user data to provide user-facing features and do not use it for any unauthorized purposes.
      </p>
    </section>

    <section class="privacy-section">
      <h2 class="section-title">8. Changes to this Privacy Policy</h2>
      <p>
        Our Privacy Policy may change occasionally, and any changes to this Policy will be updated to describe these changes. You are expected to check our website from time to time to take notice of any changes in this Policy. If you have any further questions about this Policy or how we handle your data, which are eventually not dealt with here, please contact us by writing an e-mail to support email address.
      </p>
      <p>
        Stitch
      </p>
    </section>
  </div>
</div>
